"use client"

import type React from "react"
import { Link, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Heart, Star } from "lucide-react"
import type { RootState } from "../../store/store"
import { addToCart } from "../../store/slices/cartSlice"
import { addToWishlist, removeFromWishlist } from "../../store/slices/wishlistSlice"
import type { Product } from "../../types"
import LazyImage from "./LazyImage"
import toast from "react-hot-toast"

interface ProductCardProps {
  product: Product
  className?: string
  priority?: boolean
  viewMode?: "grid" | "list"
}

// Local helpers/types to work with variant-enabled products regardless of global types.
type AnyVariant = {
  id?: string
  size?: string
  color?: string
  colorHex?: string
  priceModifier?: number
  stock?: number
  sku?: string
  isActive?: boolean
  images?: string[]
}

type AnyColor = {
  name?: string
  hex?: string
  images?: string[]
  slug?: string
}

type AnyProduct = Product & {
  images?: string[]
  variants?: AnyVariant[]
  variantColors?: AnyColor[]
  variantSizes?: { label?: string }[]
}

// Helper to slugify strings for URLs - make sure it matches ProductDetail
function slugify(str: string | undefined | null): string {
  if (!str) return ""
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// Variant helpers
function getActiveVariants(prod: AnyProduct): AnyVariant[] {
  const arr = Array.isArray((prod as AnyProduct).variants) ? (prod as AnyProduct).variants! : []
  return arr.filter((v) => v && v.isActive !== false)
}

function getFirstPreferredVariant(prod: AnyProduct): AnyVariant | undefined {
  const actives = getActiveVariants(prod)
  if (actives.length === 0) return undefined
  const inStock = actives.find((v) => (v.stock ?? 0) > 0)
  return inStock || actives[0]
}

function getVariantPrimaryImage(prod: AnyProduct, variant?: AnyVariant): string | undefined {
  if (!variant) return prod.images?.[0] || (prod as any).image
  // Prefer variant-level images
  if (Array.isArray(variant.images) && variant.images.length > 0) return variant.images[0]
  // Then color-level images
  const colors = Array.isArray(prod.variantColors) ? prod.variantColors : []
  const color = colors.find((c) => (c?.name || "").toLowerCase() === (variant.color || "").toLowerCase())
  if (color && Array.isArray(color.images) && color.images.length > 0) return color.images[0]
  // Fallback to product images
  return prod.images?.[0] || (prod as any).image
}

function getMinVariantPrice(prod: AnyProduct): number | null {
  const actives = getActiveVariants(prod)
  if (actives.length === 0) return null
  const base = Number((prod as any).price || 0)
  const modifiers = actives.map((v) => Number(v.priceModifier || 0))
  const minMod = Math.min(...modifiers)
  return base + minMod
}

function getTotalVariantStock(prod: AnyProduct): number {
  const actives = getActiveVariants(prod)
  if (actives.length === 0) return (prod as any).stock ?? 0
  return actives.reduce((sum, v) => sum + Number(v.stock || 0), 0)
}

const ProductCard: React.FC<ProductCardProps> = ({ product, className = "", priority = false, viewMode = "grid" }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const { items: wishlistItems = [] } = useSelector((state: RootState) => state.wishlist)
  const isInWishlist = wishlistItems.some((item) => item.id === product.id)

  // Helper to serialize Firestore Timestamps to avoid Redux non-serializable warnings
  const serializeTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj
    if (typeof obj !== "object") return obj
    if ("seconds" in obj && "nanoseconds" in obj) {
      // Firestore Timestamp detected
      try {
        return new Date(obj.seconds * 1000).toISOString()
      } catch {
        return obj
      }
    }
    if (Array.isArray(obj)) {
      return obj.map(serializeTimestamps)
    }
    const newObj: any = {}
    for (const key in obj) {
      newObj[key] = serializeTimestamps(obj[key])
    }
    return newObj
  }

  // Cast product to a variant-aware shape when available
  const vProduct = product as AnyProduct
  const hasVariants = Array.isArray(vProduct.variants) && vProduct.variants.length > 0
  const defaultVariant = hasVariants ? getFirstPreferredVariant(vProduct) : undefined
  const displayImage = hasVariants
    ? getVariantPrimaryImage(vProduct, defaultVariant)
    : vProduct.images?.[0] || (product as any).image
  const minVariantPrice = hasVariants ? getMinVariantPrice(vProduct) : null
  const totalStock = hasVariants ? getTotalVariantStock(vProduct) : (product.stock ?? 0)

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      toast.error("Please login to add items to cart")
      navigate("/login")
      return
    }

    if (!hasVariants) {
      // Legacy behavior: no variants
      dispatch(addToCart(serializeTimestamps(product) as any))
      toast.success("Added to cart")
      return
    }

    // With variants: if a single viable variant, add it; otherwise navigate to detail for selection
    const active = getActiveVariants(vProduct)
    if (active.length === 0) {
      toast.error("This product is currently unavailable")
      return
    }

    if (active.length > 1) {
      // To avoid silently adding the wrong option, require selection on PDP
      toast("Please select size and color", { icon: "🛍️" })
      navigate(generateProductUrl())
      return
    }

    const chosen = defaultVariant || active[0]
    const basePrice = Number((product as any).price || 0)
    const priceModifier = Number(chosen.priceModifier || 0)
    const finalUnit = basePrice + priceModifier

    const primaryCategory = (product as any).categories?.[0] || (product as any).category
    const image = getVariantPrimaryImage(vProduct, chosen)

    dispatch(
      addToCart({
        id: product.id,
        name: product.name,
        price: finalUnit,
        basePrice,
        priceModifier,
        image: image || vProduct.images?.[0] || (product as any).image,
        images: vProduct.images,
        category: primaryCategory,
        variant: {
          id: chosen.id,
          size: chosen.size,
          color: chosen.color,
          colorHex: chosen.colorHex,
          priceModifier: priceModifier,
          sku: chosen.sku,
        },
      } as any),
    )
    toast.success("Added to cart")
  }

  const handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      toast.error("Please login to continue")
      navigate("/login")
      return
    }

    if (!hasVariants) {
      dispatch(addToCart(product as any))
      navigate("/checkout")
      return
    }

    const active = getActiveVariants(vProduct)
    if (active.length !== 1) {
      toast("Please select size and color", { icon: "🛍️" })
      navigate(generateProductUrl())
      return
    }

    const chosen = defaultVariant || active[0]
    const basePrice = Number((product as any).price || 0)
    const priceModifier = Number(chosen.priceModifier || 0)
    const finalUnit = basePrice + priceModifier
    const primaryCategory = (product as any).categories?.[0] || (product as any).category
    const image = getVariantPrimaryImage(vProduct, chosen)

    dispatch(
      addToCart({
        id: product.id,
        name: product.name,
        price: finalUnit,
        basePrice,
        priceModifier,
        image: image || vProduct.images?.[0] || (product as any).image,
        images: vProduct.images,
        category: primaryCategory,
        variant: {
          id: chosen.id,
          size: chosen.size,
          color: chosen.color,
          colorHex: chosen.colorHex,
          priceModifier: priceModifier,
          sku: chosen.sku,
        },
      } as any),
    )
    navigate("/checkout")
  }

  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      toast.error("Please login to add items to wishlist")
      navigate("/login")
      return
    }

    if (isInWishlist) {
      // Remove all wishlist entries for this product (or specific one if you later key by variant)
      dispatch(removeFromWishlist(product.id) as any)
      toast.success("Removed from wishlist")
    } else {
      if (!hasVariants) {
        dispatch(addToWishlist(product as any))
        toast.success("Added to wishlist!")
      } else {
        const chosen = defaultVariant || getActiveVariants(vProduct)[0]
        const basePrice = Number((product as any).price || 0)
        const priceModifier = Number(chosen?.priceModifier || 0)
        const finalUnit = basePrice + priceModifier
        const primaryCategory = (product as any).categories?.[0] || (product as any).category
        const image = getVariantPrimaryImage(vProduct, chosen)

        dispatch(
          addToWishlist({
            id: product.id,
            name: product.name,
            price: finalUnit,
            basePrice,
            priceModifier,
            image: image || vProduct.images?.[0] || (product as any).image,
            images: vProduct.images,
            category: primaryCategory || "furniture",
            variant: chosen
              ? {
                  id: chosen.id,
                  size: chosen.size,
                  color: chosen.color,
                  colorHex: chosen.colorHex,
                  priceModifier: priceModifier,
                  sku: chosen.sku,
                }
              : undefined,
          } as any),
        )
        toast.success("Added to wishlist!")
      }
    }
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 sm:w-4 sm:h-4 ${i < Math.floor(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`}
      />
    ))
  }

  // Generate safe URL - use primary category consistently
  const generateProductUrl = () => {
    const primaryCategory = (product as any).categories?.[0] || (product as any).category || "furniture"
    const categorySlug = slugify(primaryCategory)
    const nameSlug = slugify((product as any).name) || (product as any).id
    return `/${categorySlug}/${nameSlug}`
  }

  const handleProductClick = () => {
    const url = generateProductUrl()
    console.log("Navigating to product:", {
      productId: (product as any).id,
      productName: (product as any).name,
      url,
      product,
    })
  }

  // Grid View
  if (viewMode === "grid") {
    return (
      <article
        className={`group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${className}`}
      >
        <Link to={generateProductUrl()} className="block relative group" onClick={handleProductClick}>
          {/* Image Container */}
          <div className="relative aspect-square overflow-hidden bg-gray-100">
            <LazyImage
              src={displayImage || vProduct.images?.[0] || (product as any).image}
              alt={(product as any).name || "Product"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading={priority ? "eager" : "lazy"}
              width={400}
              height={400}
            />

            {/* Badges */}
            <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col space-y-1">
              {(product as any).isNewArrival && (
                <span className="bg-newtag-100 text-newtag-600 text-xs px-2 py-1 rounded-full font-medium">New</span>
              )}
              {(product as any).isBestSeller && (
                <span className="bg-secondary-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Best Seller
                </span>
              )}
              {(product as any).originalPrice && !hasVariants && (
                <span className="bg-sale-100 text-sale-600 text-xs px-2 py-1 rounded-full font-medium">
                  {Math.round(
                    (((product as any).originalPrice - (product as any).price) / (product as any).originalPrice) * 100,
                  )}
                  % OFF
                </span>
              )}
              {hasVariants && (
                <span className="bg-gray-900 text-white text-xs px-2 py-1 rounded-full font-medium">Variants</span>
              )}
            </div>

            {/* Wishlist Button */}
            <button
              onClick={handleWishlistToggle}
              className={`absolute top-2 right-2 sm:top-3 sm:right-3 p-2 rounded-full transition-all duration-200 ${
                isInWishlist ? "bg-red-500 text-white" : "bg-white text-gray-600 hover:bg-red-500 hover:text-white"
              } shadow-md hover:shadow-lg`}
              aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
            >
              <Heart className={`w-3 h-3 sm:w-4 sm:h-4 ${isInWishlist ? "fill-current" : ""}`} />
            </button>
          </div>

          {/* Product Info */}
          <div className="p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 text-sm sm:text-base">
              {(product as any).name || "Unnamed Product"}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 capitalize mb-2">
              {(product as any).categories?.[0] || (product as any).category || "Uncategorized"}
            </p>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-1">
                {renderStars((product as any).rating || 0)}
                <span className="text-xs sm:text-sm text-gray-500">({(product as any).reviews || 0})</span>
              </div>
              {totalStock > 0 ? (
                <span className="text-xs sm:text-sm text-green-600">In Stock</span>
              ) : (
                <span className="text-xs sm:text-sm text-red-600">Out of Stock</span>
              )}
            </div>

            <div className="flex items-baseline justify-between mb-4">
              <div>
                {hasVariants && minVariantPrice != null ? (
                  <span className="text-lg sm:text-xl font-bold text-primary-600">
                    ₹{minVariantPrice.toLocaleString()}
                  </span>
                ) : (
                  <span className="text-lg sm:text-xl font-bold text-primary-600">
                    ₹{(product as any).price.toLocaleString()}
                  </span>
                )}
                {!hasVariants && (product as any).originalPrice && (
                  <span className="ml-2 text-xs sm:text-sm text-gray-500 line-through">
                    ₹{(product as any).originalPrice.toLocaleString()}
                  </span>
                )}
                {hasVariants && <span className="ml-2 text-xs text-gray-500">From</span>}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={handleAddToCart}
                disabled={totalStock === 0}
                className="flex-1 bg-primary-600 text-white py-2 px-3 sm:px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                aria-label={`Add ${(product as any).name || "product"} to cart`}
              >
                {hasVariants ? "Select & Add" : "Add to Cart"}
              </button>
              <button
                onClick={handleBuyNow}
                disabled={totalStock === 0}
                className="flex-1 bg-accent-500 text-black py-2 px-3 sm:px-4 rounded-lg hover:bg-accent-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                aria-label={`Buy ${(product as any).name || "product"} now`}
              >
                Buy Now
              </button>
            </div>
          </div>
        </Link>
      </article>
    )
  }

  // List View Layout (Horizontal)
  return (
    <article
      className={`group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${className}`}
    >
      <Link to={generateProductUrl()} className="block relative group" onClick={handleProductClick}>
        <div className="flex items-center p-4 sm:p-6">
          {/* Image Container */}
          <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0 overflow-hidden bg-gray-100 rounded-lg">
            <LazyImage
              src={displayImage || vProduct.images?.[0] || (product as any).image}
              alt={(product as any).name || "Product"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading={priority ? "eager" : "lazy"}
              width={160}
              height={160}
            />

            {/* Wishlist Button */}
            <button
              onClick={handleWishlistToggle}
              className={`absolute top-2 right-2 p-1.5 rounded-full transition-all duration-200 ${
                isInWishlist ? "bg-red-500 text-white" : "bg-white text-gray-600 hover:bg-red-500 hover:text-white"
              } shadow-md hover:shadow-lg`}
              aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
            >
              <Heart className={`w-3 h-3 ${isInWishlist ? "fill-current" : ""}`} />
            </button>
          </div>

          {/* Product Info */}
          <div className="flex-1 ml-4 sm:ml-6 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 text-base sm:text-lg">
                  {(product as any).name || "Unnamed Product"}
                </h3>
                <p className="text-sm text-gray-500 capitalize mb-2">
                  {(product as any).categories?.[0] || (product as any).category || "Uncategorized"}
                </p>
              </div>

              {/* Badges */}
              <div className="flex flex-col space-y-1 ml-4">
                {(product as any).isNewArrival && (
                  <span className="bg-newtag-100 text-newtag-600 text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                    New
                  </span>
                )}
                {(product as any).isBestSeller && (
                  <span className="bg-secondary-500 text-white text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                    Best Seller
                  </span>
                )}
                {!hasVariants && (product as any).originalPrice && (
                  <span className="bg-sale-100 text-sale-600 text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                    {Math.round(
                      (((product as any).originalPrice - (product as any).price) / (product as any).originalPrice) *
                        100,
                    )}
                    % OFF
                  </span>
                )}
                {hasVariants && (
                  <span className="bg-gray-900 text-white text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                    Variants
                  </span>
                )}
              </div>
            </div>

            {/* Rating and Stock */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-1">
                {renderStars((product as any).rating || 0)}
                <span className="text-sm text-gray-500">({(product as any).reviews || 0})</span>
              </div>
              {totalStock > 0 ? (
                <span className="text-sm text-green-600">In Stock</span>
              ) : (
                <span className="text-sm text-red-600">Out of Stock</span>
              )}
            </div>

            {/* Price and Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-baseline">
                {hasVariants && minVariantPrice != null ? (
                  <span className="text-xl sm:text-2xl font-bold text-primary-600">
                    ₹{minVariantPrice.toLocaleString()}
                  </span>
                ) : (
                  <span className="text-xl sm:text-2xl font-bold text-primary-600">
                    ₹{(product as any).price.toLocaleString()}
                  </span>
                )}
                {!hasVariants && (product as any).originalPrice && (
                  <span className="ml-2 text-sm text-gray-500 line-through">
                    ₹{(product as any).originalPrice.toLocaleString()}
                  </span>
                )}
                {hasVariants && <span className="ml-2 text-xs text-gray-500">From</span>}
              </div>

              <div className="flex space-x-2 ml-4">
                <button
                  onClick={handleAddToCart}
                  disabled={totalStock === 0}
                  className="bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                  aria-label={`Add ${(product as any).name || "product"} to cart`}
                >
                  {hasVariants ? "Select & Add" : "Add to Cart"}
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={totalStock === 0}
                  className="bg-accent-500 text-black py-2 px-4 rounded-lg hover:bg-accent-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                  aria-label={`Buy ${(product as any).name || "product"} now`}
                >
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </article>
  )
}

export default ProductCard
